import { ChangeEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { useDocumentsDataTable } from 'generated/resources/datatable.graphql'
import { DataTableFeatureFlagDocument } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import ActionsDatatable from 'modules/documents/components/ActionsDatatable'
import BulkActionsDatatable from 'modules/documents/components/BulkActionsDatatable'
import ContextFromPage from 'modules/documents/components/DatatableContextFromPage/ContextFromPage'
import DocumentsDataTableEmptyState from 'modules/documents/components/DocumentDatatableEmptyState'
import {
  createdAtFilter,
  employeeFilter,
  employeeStatusFilter,
  folderFilter,
  legalEntityFilter,
  locationFilter,
  signatureStatusFilter,
  teamFilter,
  visibleFilter,
} from 'modules/documents/components/EmployeeDocumentsDatatable/filters'
import { useGetFolders } from 'modules/documents/hooks/useGetFolders'
import { SPACES } from 'modules/documents/lib/constants'
import {
  actionsProjection,
  contentTypeProjection,
  createdAtProjection,
  employeeProjection,
  filenameProjection,
  folderProjection,
  signatureStatusProjection,
} from 'modules/documents/lib/datatable/documents/projections'
import { useTeams } from 'modules/employees/hooks/useTeams'
import { useLocations } from 'modules/locations/hooks/useLocations'

const SPACE = SPACES.EMPLOYEE_MY_DOCUMENTS
const ITEMS_PER_PAGE = 100

const EmployeeDocumentsDataTable = () => {
  const { allMyDescendants, employee: currentEmployee } = useCurrentSession()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { folders, loading: loadingFolders } = useGetFolders({ space: SPACE })
  const { teams, loading: loadingTeams } = useTeams()
  const { locations, loading: loadingLocations } = useLocations()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [isFiltering, setIsFiltering] = useState(false)

  const { loading: loadingUsers } = useAsyncData(
    () => users.fetch({ withCachedFetchParams: true }),
    []
  )

  const isLoading = loadingFolders || loadingTeams || loadingLocations || loadingUsers

  const onNavigationClick = (documentId: number) => {
    const documentUrl = navigation.files.employeeFiles.list.documentId.fullPath({
      document_id: documentId,
    })
    navigate(documentUrl)
  }

  const companyId = useCurrentSession().company?.id
  const { data: featureData } = useQuery(DataTableFeatureFlagDocument, {
    variables: { companyId: companyId ?? 0 },
    skip: companyId == null,
  })
  const deleteFeatureEnabled =
    featureData?.features?.featuresConnection?.nodes?.[0]?.enabled ?? false

  const source = useDocumentsDataTable(
    [
      contentTypeProjection(),
      filenameProjection(),
      employeeProjection(),
      folderProjection(),
      createdAtProjection(),
      signatureStatusProjection(),
      actionsProjection({
        render: (document) => (
          <ActionsDatatable
            document={document}
            onNavigationClick={onNavigationClick}
            space={SPACE}
            deleteFeatureEnabled={deleteFeatureEnabled}
          />
        ),
      }),
    ],
    { variables: { space: SPACE, search: debouncedSearch } }
  )

  const filters = [
    createdAtFilter(source),
    employeeFilter(source, users),
    employeeStatusFilter(source, currentEmployee?.id ?? 0, allMyDescendants),
    folderFilter(source, folders),
    legalEntityFilter(source),
    locationFilter(source, locations),
    signatureStatusFilter(source),
    teamFilter(source, teams),
    visibleFilter(source),
  ]

  const onDatatableSearch = (value: ChangeEvent<HTMLInputElement>) => {
    onSearch(value)

    if (value.target.value !== '') setIsFiltering(true)
    else {
      setTimeout(() => {
        setIsFiltering(false)
      }, 500)
    }
  }

  if (isLoading)
    return (
      <Box alignItems="center" padding="s64">
        <Spinner />
      </Box>
    )

  return (
    <>
      <DataTable.Provider
        source={source}
        filters={filters}
        enableClearFilters
        pagination={{
          kind: 'offset',
          itemsPerPage: ITEMS_PER_PAGE,
        }}
        sort={{
          path: 'createdAt',
          direction: 'desc',
        }}
      >
        <Box paddingY="s8" gap="s16">
          <DataTable.HeadBar
            search={{
              value: search,
              onChange: onDatatableSearch,
              placeholder: i18n.t('common.search'),
            }}
            actions={
              <BulkActionsDatatable space={SPACE} deleteFeatureEnabled={deleteFeatureEnabled} />
            }
            disableDefaultActions
          />
        </Box>
        <DataTable.Table
          selectable={() => true}
          emptyState={<DocumentsDataTableEmptyState withFilters={isFiltering} />}
        />
        <ContextFromPage parentRoute={navigation.files.employeeFiles.fullPath()} />
      </DataTable.Provider>
    </>
  )
}

export default EmployeeDocumentsDataTable
